import { _ } from "svelte-i18n";
import { get } from "svelte/store";

export type CountryData = {
  countryCode: string;
  languages?: string[];
  regionCode?: string;
  defaultLanguage?: string;
  hrefLangHide?: boolean;
};

export type RegionData = {
  regionName: string;
  regionCode: string;
  currency: string;
  currencyShort: string;
  countries: CountryData[];
};

export type LangData = {
  code: string;
  label: string;
};

export enum RegionID {
  Staging = "staging",
  UK = "uk",
  EU = "eu",
  DE = "de",
  US = "us",
  ROW = "row",
}

export const allRegions = [
  RegionID.UK,
  RegionID.EU,
  RegionID.DE,
  RegionID.US,
  RegionID.ROW,
  RegionID.Staging,
];

/** Lookup region by country code */
export function getCountryRegion(countryCode: string): RegionData {
  countryCode = countryCode.toUpperCase();
  const regions = getRegionsData();
  let regionCode = "";
  const region = regions.find(r => {
    const country = r.countries.find(c => c.countryCode === countryCode);
    // override region from country if present
    regionCode = (country && country.regionCode) || r.regionCode;
    return !!country;
  });
  return (
    region && {
      ...region,
      regionCode,
    }
  );
}

/** Lookup country data by country code */
export function getCountry(countryCode: string): CountryData | undefined {
  countryCode = countryCode.toUpperCase();
  let country;
  getRegionsData().forEach(r => {
    const c = r.countries.find(c => c.countryCode === countryCode);
    if (c) {
      country = c;
    }
  });
  return country;
}

export function getRegionsData(): RegionData[] {
  const t = get(_);
  return [
    {
      regionName: t("UK & Channel Islands"),
      regionCode: RegionID.UK,
      currency: "£ (GB Pound)",
      currencyShort: "£ GBP",
      countries: [
        { countryCode: "GB", languages: ["en"], defaultLanguage: "en" },
        { countryCode: "JE", languages: ["en"], defaultLanguage: "en" },
        { countryCode: "GG", languages: ["en"], defaultLanguage: "en" },
        { countryCode: "IM", languages: ["en"], defaultLanguage: "en" },
      ],
    },
    {
      regionName: t("REST OF WORLD"),
      regionCode: RegionID.ROW,
      currency: "$ (US Dollars)",
      currencyShort: "$ US",
      countries: [
        { countryCode: "AU", languages: ["en"], defaultLanguage: "en" },
        { countryCode: "HK", languages: ["en"], defaultLanguage: "en" },
        { countryCode: "JP", languages: ["en"], defaultLanguage: "en" },
        {
          countryCode: "KW",
          languages: ["en"],
          defaultLanguage: "en",
          hrefLangHide: true,
        },
        {
          countryCode: "CH",
          languages: ["en"],
          defaultLanguage: "en",
          hrefLangHide: true,
        },
        {
          countryCode: "AE",
          languages: ["en"],
          defaultLanguage: "en",
          hrefLangHide: true,
        },
      ],
    },
    {
      regionName: t("America & Canada"),
      regionCode: RegionID.US,
      currency: "$ (US Dollars)",
      currencyShort: "$ US",
      countries: [
        { countryCode: "US", languages: ["en"], defaultLanguage: "en" },
        { countryCode: "CA", languages: ["en"], defaultLanguage: "en" },
      ],
    },
    {
      regionName: t("Europe"),
      regionCode: RegionID.EU,
      currency: "€ (Euro)",
      currencyShort: "€ Euro",
      countries: [
        {
          countryCode: "DE",
          languages: ["en", "de"],
          regionCode: RegionID.DE,
          defaultLanguage: "de",
        },
        {
          countryCode: "AT",
          languages: ["en", "de", "es", "fr", "it"],
          hrefLangHide: true,
        },
        { countryCode: "BE", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "BG", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "HR", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "CZ", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "DK", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "EE", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "FI", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "FR", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "GR", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "HU", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "IE", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "IT", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "LV", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "LT", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "LU", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "MC", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "NL", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "NO", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "PT", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "RO", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "SK", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "SI", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "ES", languages: ["en", "de", "es", "fr", "it"] },
        { countryCode: "SE", languages: ["en", "de", "es", "fr", "it"] },
      ],
    },
  ];
}

// map languages to get translated label
export const getLanguages = (lang: string): LangData | undefined => {
  const t = get(_);

  switch (lang) {
    case "en":
      return {
        code: "en",
        label: t("English") + " - EN",
      };
    case "de":
      return {
        code: "de",
        label: t("German") + " - DE",
      };
    case "es":
      return {
        code: "es",
        label: t("Spanish") + " - ES",
      };
    case "fr":
      return {
        code: "fr",
        label: t("French") + " - FR",
      };
    case "it":
      return {
        code: "it",
        label: t("Italian") + " - IT",
      };
  }
  return;
};
